// Public Our Doctors page. The admin curates the list at
// /admin/settings/doctors; the featured doctor (`is_featured`) is pinned
// to the top as a wide dark card, the rest follow in a grid.

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import { useListDoctorsQuery } from '../store/siteContentApi';
import { Arrow } from '../components/home/Hero';
import {
  UI, DISPLAY, EASE, wrap, phero, pheroIn, hXl, hL, hS, hXs, lead, eyebrow, sec,
  empty, card, cardDark, chipTeal, flagYellow, pill, btn, iconbtn, prose, sk, textbtn,
} from '../styles/tl5';

// Drop the honorific first — "Dr. Charu Bali" was initialling to "DC".
const HONORIFIC = /^(dr|mr|mrs|ms|miss|prof|capt)\.?$/i;
const initialsOf = (name) => (name || '')
  .split(/\s+/)
  .filter((part) => part && !HONORIFIC.test(part))
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase() || '')
  .join('');

// Circular initials avatar, with the photo laid over it when the admin has
// uploaded one. Initials remain underneath so a broken URL still reads.
const Avatar = ({ src, name, size = 64, dark = false }) => (
  <Box
    aria-hidden="true"
    sx={{
      display: 'grid',
      placeItems: 'center',
      width: size,
      height: size,
      flex: 'none',
      borderRadius: '50%',
      overflow: 'hidden',
      background: dark ? 'rgba(95,224,198,.14)' : 'var(--tl-mint)',
      color: dark ? 'var(--tl-teal-l)' : 'var(--tl-g9)',
      fontFamily: DISPLAY,
      fontSize: size * 0.34,
      fontWeight: 800,
      ...(src ? { backgroundImage: `url("${src}")`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' } : null),
    }}
  >
    {initialsOf(name)}
  </Box>
);

const CloseIcon = (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"
    sx={{ width: 18, height: 18, display: 'block' }}
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </Box>
);

const OurDoctors = () => {
  const { data: doctors = [], isLoading } = useListDoctorsQuery();
  const [bioOf, setBioOf] = useState(null);

  const featured = doctors.find((d) => d.is_featured) || null;
  const rest = featured ? doctors.filter((d) => d._id !== featured._id) : doctors;

  return (
    <Box>
      {/* ═══ HERO ═══ */}
      <Box component="section" sx={phero}>
        <Box sx={{ ...wrap, ...pheroIn, gap: '14px' }}>
          <Box component="span" sx={eyebrow}>Our doctors</Box>
          <Typography component="h1" sx={hXl}>Meet the team behind every report.</Typography>
          <Typography sx={{ ...lead, maxWidth: 560 }}>
            NABL-accredited pathologists verify every result before it reaches you.
          </Typography>
        </Box>
      </Box>

      {/* ═══ TEAM ═══ */}
      <Box component="section" sx={sec}>
        <Box sx={wrap}>
          {isLoading && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: '16px' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} sx={{ ...card, p: '24px' }}>
                  <Box sx={{ ...sk, width: 64, height: 64, borderRadius: '50%' }} />
                  <Box sx={{ ...sk, width: '70%', height: 18, borderRadius: '6px', mt: '16px' }} />
                  <Box sx={{ ...sk, width: '50%', height: 14, borderRadius: '4px', mt: '10px' }} />
                  <Box sx={{ ...sk, width: 96, height: 30, borderRadius: 999, mt: '14px' }} />
                </Box>
              ))}
            </Box>
          )}

          {!isLoading && doctors.length === 0 && (
            <Box sx={empty}>
              <Typography sx={{ fontSize: 30, lineHeight: 1 }}>🩺</Typography>
              <Typography component="h2" sx={hS}>Our team is being listed</Typography>
              <Typography sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted)', maxWidth: 380 }}>
                Every TrustLab report is signed off by an NABL-accredited pathologist.
              </Typography>
            </Box>
          )}

          {/* ── the lab head ── */}
          {!isLoading && featured && (
            <Box
              sx={{
                ...cardDark,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
                gap: 'clamp(20px, 3vw, 44px)',
                alignItems: 'center',
                p: 'clamp(22px, 3vw, 36px)',
                borderRadius: '36px',
                mb: '20px',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  placeItems: 'center',
                  minHeight: 240,
                  borderRadius: '28px',
                  background: 'rgba(255,255,255,.03)',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    border: '1px dashed rgba(95,224,198,.35)',
                    animation: `tl-spin 60s linear infinite`,
                    pointerEvents: 'none',
                  },
                }}
              >
                <Avatar src={featured.photo_url} name={featured.name} size={132} dark />
              </Box>

              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                <Box component="span" sx={flagYellow}>{featured.title || 'Head of the lab'}</Box>
                <Typography component="h2" sx={{ ...hL, color: '#fff' }}>{featured.name}</Typography>
                {featured.qualifications && (
                  <Typography sx={{ fontFamily: UI, fontSize: 16, color: '#B6CFC8' }}>
                    {featured.qualifications}
                  </Typography>
                )}
                {featured.bio && (
                  <Typography
                    sx={{
                      fontFamily: UI, fontSize: 15.5, lineHeight: 1.6, color: '#B6CFC8',
                      display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden',
                    }}
                  >
                    {featured.bio}
                  </Typography>
                )}
                {featured.bio && (
                  <Box component="button" type="button" onClick={() => setBioOf(featured)} sx={{ ...pill, border: 0, mt: '4px' }}>
                    Read full bio
                    <Arrow size={15} />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ── the rest ── */}
          {!isLoading && rest.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: '16px' }}>
              {rest.map((d, i) => (
                <Box
                  key={d._id}
                  sx={{
                    ...card,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    p: '24px',
                    animation: `tl-stgin .5s ${EASE} both`,
                    animationDelay: `${Math.min(i, 6) * 55}ms`,
                  }}
                >
                  <Avatar src={d.photo_url} name={d.name} size={64} />
                  <Typography component="h2" sx={hXs}>{d.name}</Typography>
                  {d.qualifications && (
                    <Typography sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted)' }}>
                      {d.qualifications}
                    </Typography>
                  )}
                  {d.title && (
                    <Box component="span" sx={{ ...chipTeal, alignSelf: 'flex-start' }}>{d.title}</Box>
                  )}
                  {d.bio && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setBioOf(d)}
                      sx={{ ...textbtn, display: 'inline-flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-start', mt: 'auto', px: 0 }}
                    >
                      Read more
                      <Arrow size={14} />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* ═══ BIO DIALOG ═══ */}
      <Dialog
        open={!!bioOf}
        onClose={() => setBioOf(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 'var(--tl-r-xl)', background: '#fff', m: 2 } }}
      >
        {bioOf && (
          <Box sx={{ p: 'clamp(22px, 4vw, 36px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <Avatar src={bioOf.photo_url} name={bioOf.name} size={60} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography component="h2" sx={hS}>{bioOf.name}</Typography>
                {bioOf.qualifications && (
                  <Typography sx={{ fontFamily: UI, fontSize: 14.5, color: 'var(--tl-muted)', mt: '2px' }}>
                    {bioOf.qualifications}
                  </Typography>
                )}
              </Box>
              <Box component="button" type="button" onClick={() => setBioOf(null)} aria-label="Close" sx={iconbtn}>
                {CloseIcon}
              </Box>
            </Box>

            {bioOf.title && <Box component="span" sx={{ ...chipTeal, mt: '16px' }}>{bioOf.title}</Box>}
            <Typography sx={{ ...prose, mt: '14px' }}>{bioOf.bio}</Typography>

            <Box component="button" type="button" onClick={() => setBioOf(null)} sx={{ ...btn, width: '100%', mt: '24px' }}>
              Close
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default OurDoctors;
